import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, 
  Clock, 
  CheckCircle, 
  Sparkles, 
  TrendingDown,
  ArrowRight,
  Target,
  Star,
  Users,
  Shield,
  Zap,
  Check,
  User,
  Euro,
  BookOpen,
  Building2,
  PlayCircle,
  Gift,
  Award,
  Brain,
  PiggyBank,
  Lightbulb,
  Heart,
  Smartphone,
  Globe,
  BarChart3
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogClose
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { PricingSection } from '@/components/PricingSection';
import { useAuth } from '@/contexts/AuthContext';
import { MotivationalQuote } from '@/components/MotivationalQuote';
import { Roadmap } from '@/components/Roadmap';
import { PersonalStory } from '@/components/PersonalStory';
import { CookieBanner } from '@/components/CookieBanner';
import { InteractiveDemo } from '@/components/InteractiveDemo';
import { FAQItem } from '@/components/FAQItem';
import { OnboardingFlow } from '@/components/OnboardingFlow';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import confetti from 'canvas-confetti';

interface Profile {
  id: string;
  full_name: string;
  email: string;
  custom_full_name?: boolean;
}

const Index = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const navigate = useNavigate();
  const { showAuthModal, user, signOut } = useAuth();

  // Profil laden wenn Benutzer angemeldet ist
  useEffect(() => {
    if (user) {
      fetchUserProfile();
    } else {
      setProfile(null);
    }
  }, [user]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      setProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Funktion um den korrekten Benutzernamen zu ermitteln
  const getUserDisplayName = () => {
    if (!user) return 'Nutzer';
    
    // Priorität: 1. Profil-Name (falls custom), 2. Google-Name, 3. E-Mail-Fallback
    if (profile?.full_name && profile?.custom_full_name) {
      return profile.full_name.split(' ')[0];
    }
    
    if (profile?.full_name) {
      return profile.full_name.split(' ')[0];
    }
    
    return user.user_metadata?.full_name?.split(' ')[0] || 
           user.user_metadata?.name?.split(' ')[0] || 
           user.email?.split('@')[0] || 
           'Nutzer';
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setIsOnboardingOpen(true);
    }
  };

  const handleSignIn = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      showAuthModal('login');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleTestUser = () => {
    navigate('/demo');
  };

  const handleUpgrade = () => {
    navigate('/auth');
  };

  const openVideoOverlay = () => {
    setIsVideoOpen(true);
  };

  // Feedback-Animationen für Gespart/Gekauft Buttons
  const handleSavedClick = (event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x, y },
      colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
      shapes: ['circle'],
      scalar: 0.8,
      gravity: 0.6,
      drift: 0.1
    });

    setTimeout(() => {
      confetti({
        particleCount: 20,
        spread: 40,
        origin: { x, y },
        colors: ['#10B981', '#059669'],
        scalar: 0.6
      });
    }, 200);
  };

  const handleBoughtClick = (event: React.MouseEvent) => {
    const button = event.target as HTMLElement;
    button.classList.add('animate-pulse');
    setTimeout(() => button.classList.remove('animate-pulse'), 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 -translate-y-0.5"
              />
              <span className="text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
              </span>
            </a>
          </div>
          
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            {!user && (
              <button
                onClick={() => navigate('/demo')}
                className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <PlayCircle className="w-4 h-4" />
                Demo testen
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Mobile: Icon-Links für Blog und Institutionen */}
            <div className="flex items-center gap-1 sm:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/blog')}
                className="w-8 h-8 p-0 hover:bg-gray-50"
                title="Blog"
              >
                <BookOpen className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/institutionen')}
                className="w-8 h-8 p-0 hover:bg-gray-50"
                title="Für Institutionen"
              >
                <Building2 className="w-4 h-4" />
              </Button>
            </div>
            
            {user ? (
              <>
                <span className="text-sm text-gray-600 hidden sm:block">
                  {getUserDisplayName()}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-gray-600 hover:text-gray-800 hidden sm:flex"
                >
                  Abmelden
                </Button>
                <Button
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={handleGetStarted}
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-gray-600 hover:text-emerald-600 hidden sm:flex"
                  onClick={handleSignIn}
                >
                  Anmelden
                </Button>
                <Button
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={handleGetStarted}
                >
                  Kostenlos starten
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-emerald-100/30 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-20 w-40 h-40 bg-blue-100/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-1/3 w-36 h-36 bg-purple-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          {user && (
            <div className="mb-8 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200/50 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-emerald-900">
                      Willkommen zurück, {getUserDisplayName()}! 👋
                    </h3>
                    <p className="text-sm text-emerald-700">
                      Bereit für bewusstes Konsumieren?
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 rounded-xl"
                >
                  Abmelden
                </Button>
              </div>
            </div>
          )}

          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-all duration-300 border-emerald-200 rounded-xl px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Für Freelancer, Selbstständige & Privatpersonen
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Schluss mit{' '}
              <span className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 bg-clip-text text-transparent">
                Impulskäufen
              </span>
              <br />
              <span className="text-gray-700 font-light">Hallo bewusstes Konsumieren</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              KaufPause hilft dir dabei, zwischen "Business Essential" und "Nice-to-Have" zu unterscheiden.{' '}
              <span className="font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-xl border border-emerald-200 whitespace-nowrap">
                30 Tage Bedenkzeit
              </span>{' '}
              vor jedem Kauf – deine Geldbörse wird es dir danken.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              {user ? (
                <Button 
                  size="lg" 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group rounded-xl"
                >
                  Zum Dashboard
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group rounded-xl"
                  >
                    KaufPause setzen 
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                  <Button 
                    variant="outline"
                    size="lg"
                    onClick={openVideoOverlay}
                    className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-4 text-lg transition-all duration-300 hover:scale-105 flex items-center rounded-xl"
                  >
                    <PlayCircle className="w-5 h-5 mr-2" />
                    In 60 Sek. erklärt
                  </Button>
                </>
              )}
            </div>

            {/* Trust indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex flex-col items-center justify-center gap-2 p-5 bg-white rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                    <Euro className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800 text-center">Keine versteckten Kosten</span>
                  <span className="text-xs text-gray-500 text-center">Transparent & fair</span>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex flex-col items-center justify-center gap-2 p-5 bg-white rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800 text-center">In 2 Minuten startklar</span>
                  <span className="text-xs text-gray-500 text-center">Sofort nutzbar</span>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex flex-col items-center justify-center gap-2 p-5 bg-white rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800 text-center">Über 15 Blogartikel</span>
                  <span className="text-xs text-gray-500 text-center">Tipps & Wissen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
                      <div className="relative group max-w-5xl mx-auto">
            <div className="relative">
              <img 
                src="/screenshots/Dashboard Warteliste.jpeg" 
                alt="KaufPause Dashboard - Warteliste Übersicht"
                className="w-full rounded-2xl shadow-2xl border border-gray-200/50 transition-all duration-300 hover:shadow-3xl hover:scale-[1.02]"
              />
              {/* Hover Overlay Text */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center">
                <div className="text-white text-center px-4 sm:px-8 max-w-xl">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">Dein persönliches Dashboard</h3>
                  <p className="text-sm sm:text-base leading-tight">Behalte alle deine Wunschprodukte im Blick und<br className="hidden sm:block"/>verfolge deinen Fortschritt beim bewussten Konsumieren</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* So funktioniert's */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              So funktioniert's
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Der bewährte 30-Tage-Prozess für bewusste Kaufentscheidungen
            </p>
          </div>
        
          {/* Timeline Container */}
          <div className="max-w-5xl mx-auto">
            {/* Mobile: Vertical Timeline */}
            <div className="md:hidden space-y-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      1
                    </div>
                    <div className="w-0.5 h-16 bg-emerald-200 mx-auto mt-2"></div>
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100">
                      <div className="flex items-center gap-3 mb-3">
                        <Clock className="w-6 h-6 text-emerald-600" />
                        <h3 className="text-lg font-bold text-gray-900">Produkt hinzufügen</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Du findest etwas, das du kaufen möchtest? Füge es zu deiner KaufPause-Liste hinzu. 
                        Der 30-Tage-Timer startet automatisch.
                      </p>
                      <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-emerald-800 text-xs font-medium">
                          Tipp: Je emotionaler der Kaufwunsch, desto wichtiger die Bedenkzeit!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      2
                    </div>
                    <div className="w-0.5 h-16 bg-blue-200 mx-auto mt-2"></div>
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <Target className="w-6 h-6 text-blue-600" />
                        <h3 className="text-lg font-bold text-gray-900">30 Tage reflektieren</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Während der Wartezeit denkst du über den Kauf nach. Brauchst du es wirklich? 
                        Oft verschwindet der Kaufimpuls von selbst.
                      </p>
                      <div className="mt-4 flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full w-3/4 transition-all duration-1000"></div>
                        </div>
                        <span className="text-xs text-gray-500 font-medium">22 Tage</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      3
                    </div>
                    <div className="w-0.5 h-16 bg-green-200 mx-auto mt-2"></div>
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-green-100">
                      <div className="flex items-center gap-3 mb-3">
                        <Sparkles className="w-6 h-6 text-green-600" />
                        <h3 className="text-lg font-bold text-gray-900">Bewusst entscheiden</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Nach 30 Tagen triffst du eine bewusste Entscheidung: Kaufen oder Geld sparen? 
                        Du wirst überrascht sein, wie oft die Antwort "Sparen" lautet.
                      </p>
                      <div className="mt-4 flex gap-2">
                        <button 
                          onClick={handleSavedClick}
                          className="flex-1 p-2 bg-green-50 rounded-lg border border-green-200 text-center hover:bg-green-100 transition-all duration-200 hover:scale-105 active:scale-95"
                        >
                          <span className="text-green-800 text-xs font-medium">Gespart</span>
                        </button>
                        <button 
                          onClick={handleBoughtClick}
                          className="flex-1 p-2 bg-blue-50 rounded-lg border border-blue-200 text-center hover:bg-blue-100 transition-all duration-200 hover:scale-105 active:scale-95"
                        >
                          <span className="text-blue-800 text-xs font-medium">Gekauft</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Horizontal Timeline */}
            <div className="hidden md:block">
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-emerald-200 via-blue-200 to-green-200 rounded-full"></div>
                
                {/* Timeline Steps */}
                <div className="grid grid-cols-3 gap-8">
                  {/* Step 1 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="relative inline-block">
                        <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-xl relative z-10">
                          1
                        </div>
                        <div className="absolute inset-0 bg-emerald-600 rounded-full animate-ping opacity-20" style={{ animationDuration: '3s' }}></div>
                      </div>
                      <div className="mt-8">
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-emerald-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Produkt hinzufügen</h3>
                          </div>
                          <p className="text-gray-600 leading-relaxed text-center">
                            Füge Wunschprodukte zu deiner Liste hinzu. Der 30-Tage-Timer startet automatisch.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="relative inline-block">
                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-xl relative z-10">
                          2
                        </div>
                        <div className="absolute inset-0 bg-blue-600 rounded-full animate-ping opacity-20" style={{ animationDelay: '0.5s', animationDuration: '3s' }}></div>
                      </div>
                      <div className="mt-8">
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-blue-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900">30 Tage reflektieren</h3>
                          </div>
                          <p className="text-gray-600 leading-relaxed text-center mb-4">
                            Denke in Ruhe über den Kauf nach. Oft verschwindet der Impuls von selbst.
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <span>Tag 1</span>
                              <span>Tag 30</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full w-3/4 transition-all duration-1000 relative">
                                <div className="absolute right-0 top-0 w-3 h-3 bg-blue-600 rounded-full animate-pulse"></div>
                              </div>
                            </div>
                            <p className="text-center text-blue-600 font-medium text-sm">22 Tage verbleibend</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="relative inline-block">
                        <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-xl relative z-10">
                          3
                        </div>
                        <div className="absolute inset-0 bg-green-600 rounded-full animate-ping opacity-20" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
                      </div>
                      <div className="mt-8">
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-green-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Bewusst entscheiden</h3>
                          </div>
                          <p className="text-gray-600 leading-relaxed text-center mb-4">
                            Nach 30 Tagen entscheidest du bewusst: Kaufen oder sparen?
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <button 
                              onClick={handleSavedClick}
                              className="p-3 bg-green-50 rounded-xl border border-green-200 text-center hover:bg-green-100 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md"
                            >
                              <span className="text-green-800 text-sm font-medium">Gespart</span>
                            </button>
                            <button 
                              onClick={handleBoughtClick}
                              className="p-3 bg-blue-50 rounded-xl border border-blue-200 text-center hover:bg-blue-100 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md"
                            >
                              <span className="text-blue-800 text-sm font-medium">Gekauft</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-8 border border-emerald-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Bereit für bewusstere Kaufentscheidungen?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Starte noch heute mit deiner ersten 30-Tage-Bedenkzeit und erlebe selbst, 
                wie sich dein Kaufverhalten positiv verändert.
              </p>
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group rounded-xl"
              >
                {user ? 'Zum Dashboard' : 'Jetzt kostenlos testen'}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Screenshots */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Entdecke alle Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Von der Impulskontrolle bis zur Abo-Verwaltung - KaufPause bietet alles für bewusstes Konsumieren
            </p>
          </div>

          <div className="space-y-20 max-w-6xl mx-auto">
            {/* Feature 1: Impuls-Check */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src="/screenshots/Impuls Check Screenshot-min.jpeg" 
                  alt="Impuls-Check Feature"
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl border border-gray-200 transition-all duration-500 hover:shadow-3xl hover:scale-[1.05] hover:-translate-y-2 cursor-pointer"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6 flex flex-col justify-center max-w-md">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Impuls-Check
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Akuter Kaufimpuls? Unser intelligenter Impuls-Check aktiviert einen 24h Cool-Down Timer 
                  und unterstützt dich mit KI-gestützten Fragen dabei, den Kaufimpuls bewusst zu managen.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">24h Cool-Down Timer bei Kaufimpulsen</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">KI-gestützte Reflexionsfragen</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Intelligentes Impulskauf-Management</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2: Abo-Verwaltung */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 flex flex-col justify-center max-w-md ml-auto lg:ml-16">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Abo-Verwaltung
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Behalte alle deine Abonnements im Blick und vergiss nie wieder eine Kündigung. 
                  Verwalte Netflix, Spotify und Co. an einem Ort und sieh sofort deine monatlichen Kosten.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Alle Abos übersichtlich verwalten</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Automatische Kostenberechnung</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Erinnerungen für Kündigungsfristen</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <img 
                  src="/screenshots/Meine Abonnemtos Screenshot-min.jpeg" 
                  alt="Abo-Verwaltung Feature"
                  className="w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 transition-all duration-500 hover:shadow-3xl hover:scale-[1.05] hover:-translate-y-2 cursor-pointer"
                />
              </div>
            </div>

            {/* Feature 3: Warteliste */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src="/screenshots/Neues Produkt Screenshot-min.jpeg" 
                  alt="Warteliste Feature"
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl border border-gray-200 transition-all duration-500 hover:shadow-3xl hover:scale-[1.05] hover:-translate-y-2 cursor-pointer"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6 flex flex-col justify-center max-w-md">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  30-Tage Warteliste
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Füge Wunschprodukte zu deiner Warteliste hinzu und warte 30 Tage ab. 
                  Du wirst überrascht sein, wie oft sich der anfängliche Kaufwunsch in Luft auflöst.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Einfaches Hinzufügen von Produkten</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Automatischer 30-Tage Timer</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Bewusste Kaufentscheidung nach Bedenkzeit</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for target groups */}
      <section className="bg-white/50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfekt für dich, wenn du...
            </h2>
            <p className="text-xl text-gray-600">
              KaufPause hilft verschiedenen Zielgruppen beim bewussten Konsumieren
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-gradient-to-br from-white to-emerald-50">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-emerald-800">
                  <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300">
                    <Users className="w-5 h-5" />
                  </div>
                  Freelancer bist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                    <span>Unterscheide zwischen Business-Tools und Nice-to-Have</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                    <span>Vermeide teure Tool-Sammlungen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                    <span>Setze Prioritäten bei Business-Ausgaben</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-gradient-to-br from-white to-blue-50">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-blue-800">
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <Shield className="w-5 h-5" />
                  </div>
                  Selbstständig bist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span>Kontrolliere deine Geschäftsausgaben</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span>Optimiere dein Cashflow-Management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span>Plane Investitionen strategisch</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-gradient-to-br from-white to-purple-50">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-purple-800">
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition-colors duration-300">
                    <Heart className="w-5 h-5" />
                  </div>
                  Bewusst leben willst
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Reduziere Impulskäufe nachhaltig</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Entwickle bewusste Konsumgewohnheiten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Spare Geld für wichtige Ziele</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live Demo Section - Remove border */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Teste KaufPause sofort
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Keine Anmeldung nötig - probiere alle Features direkt aus
            </p>
          </div>
          
          <InteractiveDemo />
        </div>
      </section>

      {/* Kennst du das Problem */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kennst du das Problem?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Problem Card 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-red-100 hover:-translate-y-1">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">🛍️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Spontankäufe
              </h3>
              <p className="text-gray-600 text-sm">
                "Das brauche ich!" - 3 Klicks später bereust du es.
              </p>
            </div>

            {/* Problem Card 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-orange-100 hover:-translate-y-1">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">💻</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Workflow-Gadgets
              </h3>
              <p className="text-gray-600 text-sm">
                "Das macht mich produktiver!" - Spoiler: Tut es nicht.
              </p>
            </div>

            {/* Problem Card 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-yellow-100 hover:-translate-y-1">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">⏰</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Flash-Deals
              </h3>
              <p className="text-gray-600 text-sm">
                Künstlicher Zeitdruck lässt uns schlecht entscheiden.
              </p>
            </div>

            {/* Problem Card 4 */}
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-purple-100 hover:-translate-y-1">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">📦</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Ungenutzte Käufe
              </h3>
              <p className="text-gray-600 text-sm">
                Gadgets verstauben, Kurse bleiben ungemacht.
              </p>
            </div>
          </div>

          {/* Call to Action für Problem Section */}
          <div className="text-center mt-12">
            <div className="bg-white rounded-xl p-6 shadow-md max-w-2xl mx-auto border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Schluss mit Impulskäufen!
              </h3>
              <p className="text-gray-600 mb-4">
                KaufPause gibt dir 30 Tage Bedenkzeit.
              </p>
              <Button 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white px-6 py-2 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Jetzt ausprobieren <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Motivational Quote */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <MotivationalQuote />
        </div>
      </section>

      {/* Personal Story */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <PersonalStory />
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Was kommt als Nächstes?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unsere Roadmap für die kommenden Features
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Roadmap />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 via-white to-emerald-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Häufig gestellte Fragen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Antworten auf die wichtigsten Fragen zu KaufPause
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  question: "Wie funktioniert die 30-Tage-Regel?",
                  answer: "Jedes Produkt, das du hinzufügst, wird automatisch für 30 Tage \"eingefroren\". Du kannst es in dieser Zeit nicht kaufen, sondern nur darüber nachdenken. Nach 30 Tagen entscheidest du bewusst: Brauchst du es wirklich oder war es nur ein Impuls?",
                  color: "emerald" as const
                },
                {
                  question: "Ist die kostenlose Version wirklich kostenlos?",
                  answer: "Ja, absolut! Du kannst KaufPause mit 1 aktiven Produkt komplett kostenlos nutzen - ohne versteckte Kosten, ohne zeitliche Begrenzung. Nur wenn du mehr Produkte gleichzeitig verwalten möchtest, benötigst du Premium.",
                  color: "blue" as const
                },
                {
                  question: "Was passiert mit meinen Daten?",
                  answer: "Deine Daten gehören dir. Wir speichern nur die notwendigen Informationen (Produktname, Preis, Kategorie) um die App funktionsfähig zu halten. Keine Verkäufe, keine Weitergabe an Dritte. Details findest du in unserer Datenschutzerklärung.",
                  color: "purple" as const
                },
                {
                  question: "Kann ich die App auf mehreren Geräten nutzen?",
                  answer: "Ja, deine Daten werden automatisch synchronisiert. Du kannst KaufPause auf deinem Smartphone, Tablet und Computer nutzen - alles ist immer auf dem aktuellen Stand.",
                  color: "orange" as const
                },
                {
                  question: "Gibt es eine automatische Verlängerung?",
                  answer: "Nein! Premium-Lizenzen sind Einmalzahlungen für ein Jahr. Es gibt keine automatische Verlängerung und keine Abo-Falle. Nach einem Jahr läuft dein Premium automatisch aus und du entscheidest neu.",
                  color: "green" as const
                },
                {
                  question: "Was ist, wenn ich eine Frage habe?",
                  answer: "Schreib uns einfach eine E-Mail an kontakt@kaufpause.at. Wir antworten normalerweise innerhalb von 24 Stunden und helfen gerne bei allen Fragen rund um KaufPause.",
                  color: "red" as const
                }
              ].map((faq, index) => (
                <FAQItem key={index} {...faq} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50/30 via-white to-emerald-50/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Wähle deinen Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Starte kostenlos oder hole dir Premium für mehr Features
            </p>
          </div>
          <PricingSection />
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Moderner Gradient Hintergrund */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-green-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-emerald-400/10 via-transparent to-green-600/10"></div>
        
        {/* Dekorative Elemente */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-400/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10 text-white">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Bereit für bewussteren Konsum?
          </h2>
          <p className="text-xl mb-12 opacity-90 max-w-2xl mx-auto">
            Starte noch heute deine 30-Tage-Testphase und entdecke, wie viel du sparen kannst.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-4 text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 rounded-xl"
            >
              Jetzt kostenlos ausprobieren <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <div className="text-emerald-100 text-sm">
              ✓ Keine Kreditkarte erforderlich<br />
              ✓ Sofort nutzbar
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="text-2xl font-bold mb-4">KaufPause</div>
                <p className="text-gray-400 mb-4">
                  Bewusster konsumieren, mehr sparen, bewusster leben.
                </p>
                <p className="text-gray-500 text-sm">
                  Die App, die dir hilft, Impulskäufe zu vermeiden und deine Sparziele zu erreichen.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Rechtliches</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>
                    <button 
                      onClick={() => navigate('/privacy')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      Datenschutz
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/imprint')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      Impressum
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/terms')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      AGB
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Weitere Inhalte</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>
                    <button 
                      onClick={() => navigate('/blog')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      Blog
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/institutionen')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      Für Institutionen
                    </button>
                  </li>
                  <li>
                    <a href="mailto:kontakt@kaufpause.at" className="hover:text-white transition-colors">
                      kontakt@kaufpause.at
                    </a>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/demo')} 
                      className="hover:text-white transition-colors text-left"
                    >
                      Demo testen
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 pt-8 text-center">
              <div className="text-sm text-gray-500">
                © 2025 KaufPause. Alle Rechte vorbehalten.
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Cookie Banner */}
      <CookieBanner />

      {/* Onboarding Flow */}
      <OnboardingFlow 
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
      />

      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader className="text-lg font-semibold">
            KaufPause - Impulskäufe vermeiden
            <DialogClose className="absolute right-4 top-4">
              <X className="h-4 w-4" />
              <span className="sr-only">Schließen</span>
            </DialogClose>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden rounded-md">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube-nocookie.com/embed/vU5p-zZHPMg"
              title="KaufPause - Impulskäufe vermeiden"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="rounded-md"
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
